import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { LoaderSkeleton, SingleLineText, Title, WithErrorBoundary } from '@bloomays-lib/ui.shared';
import { InscriptionBloomer } from '../organisms/InscriptionBloomer';
import { notify, cancel } from '../../helpers/toastify';

import {
  CHECK_INVITATION_TOKEN,
  ME,
  UPSERT_BLOOMER,
  FINISH_INVITATION,
  UPSERT_BLOOMER_SOCIETY,
} from '@bloomays-lib/adapter.api-bloomer';

import { FallbackError } from '@bloomays-lib/ui.shared';
import { BrowserBloomer, ISociety } from '@bloomays-lib/types.shared';
import { errorLogger } from '../../helpers/error';
import { Logger } from '../../services/serviceLogger';
import { useUserStore } from '../../stores';

const logger = Logger('BloomerInvitation');

const BloomerInvitation = () => {
  const { token } = useParams<{ token: string }>();
  const [uploading, setUploading] = useState(false);
  const dataMe = useUserStore((state) => state.user);

  if (!token) {
    throw new Error('Missing token params');
  }

  const [registration, setRegistration] = useState<{ bloomer?: BrowserBloomer; society?: ISociety }>({
    bloomer: undefined,
    society: undefined,
  });
  const [upsertBloomer] = useMutation<{ upsertBloomer: BrowserBloomer }>(UPSERT_BLOOMER, {
    refetchQueries: [
      {
        query: ME,
      },
    ],
  });
  const [finishInvitation] = useMutation<{ finishInvitation: boolean }, { token: string }>(FINISH_INVITATION, {
    variables: { token: token },
    refetchQueries: [
      {
        query: ME,
      },
      {
        query: CHECK_INVITATION_TOKEN,
        variables: { token: token },
      },
    ],
  });
  const [upsertBloomerSociety] = useMutation<{
    upsertBloomerSociety: ISociety;
  }>(UPSERT_BLOOMER_SOCIETY, {
    refetchQueries: [
      {
        query: ME,
      },
    ],
  });
  const {
    data: dataInvitation,
    error: errorInvitation,
    loading: loadingInviation,
  } = useQuery<{
    checkInvitationToken: {
      recordId: string;
      status: 'done' | 'not started' | 'on going';
      mission: string[];
    };
  }>(CHECK_INVITATION_TOKEN, {
    variables: { token: token },
  });

  const missionfForSociety = dataInvitation?.checkInvitationToken.mission;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      if (registration.bloomer && registration.society) {
        setUploading(true);
        notify('info', 'Upload en cours de tes données!', null, {
          hideProgressBar: true,
          autoClose: undefined,
          toastId: 'uploadOnboarding',
        });
        try {
          const bloomer = registration.bloomer;
          await upsertBloomer({
            variables: {
              input: {
                recordId: bloomer.recordId,
                firstname: bloomer.firstname,
                lastname: bloomer.lastname,
                email: bloomer.email,
                secondaryEmail: bloomer.secondaryEmail,
                phoneNumber: bloomer.phoneNumber,
                slack: bloomer.slack,
                accountManager: [bloomer.accountManagerId],
                credentialID: bloomer.credentialID,
                communicationType: bloomer?.communicationType,
              },
            },
          });
          const society = registration.society;
          await upsertBloomerSociety({
            variables: {
              input: {
                name: society.name,
                recordId: society.recordId,
                status: society.status,
                siret: society.siret,
                KBIS: society.KBIS,
                KbisExpirationDate: society.KbisExpirationDate,
                URSSAFVigilanceCertificate: society.URSSAFVigilanceCertificate,
                URSSAFVCExpirationDate: society.URSSAFVCExpirationDate,
                RCPVigilanceCertificate: society.RCPVigilanceCertificate,
                RCPExpirationDate: society.RCPExpirationDate,
                bloomerBillingMission: missionfForSociety,
                IBAN: society.IBAN,
                BIC: society.BIC,
                portage: society.portage || false,
                tva: society.tva,
                addressCountryCode: society.addressCountryCode,
              },
            },
          });
          await finishInvitation();
          cancel('uploadOnboarding');
          notify('success', 'Ton profil a bien été mis à jour ! :)', null, {
            hideProgressBar: true,
            autoClose: undefined,
          });
          document.location.href = '/dashboard';
        } catch (error: any) {
          setUploading(false);
          cancel();
          notify('error', 'Profil non mis à jour 😓', error);
          errorLogger(error, {
            extraInfos: 'extraErrorInfos',
            state: registration,
          });
          setRegistration({ bloomer: undefined, society: undefined });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration.bloomer, registration.society]);

  const upsertBloomerData = async (bloomer: BrowserBloomer): Promise<BrowserBloomer> => {
    await setRegistration((previousSate) => ({
      ...previousSate,
      bloomer: bloomer,
    }));
    return bloomer;
  };

  const upsertSocietyData = async (society: ISociety): Promise<ISociety> => {
    await setRegistration((previousSate) => ({
      ...previousSate,
      society: society,
    }));
    return society;
  };

  useEffect(() => {
    if (!errorInvitation) {
      return;
    }
    errorLogger(errorInvitation, {
      extraInfos: 'Impossible to retrieve infos, error checking invitation token.',
      state: {},
    });
  }, [errorInvitation]);

  logger.debug('on render', { registration, dataInvitation, uploading, dataMe, missionfForSociety });

  if (dataInvitation?.checkInvitationToken?.status === 'done') {
    document.location.href = '/dashboard';
    return (<LoaderSkeleton height={600} width={800} />) as React.ReactNode;
  }

  if (errorInvitation) {
    return (<FallbackError errorText="Ton invitation ne semble pas valide !" />) as React.ReactNode;
  }

  if (loadingInviation) {
    return (<LoaderSkeleton height={600} width={800} />) as React.ReactNode;
  }

  if (dataInvitation?.checkInvitationToken.status === 'on going' && dataMe && dataMe?.bloomer) {
    return (
      <div>
        <Title textTitle="Bienvenue sur notre outil !" />
        <SingleLineText text="Voici un petit questionnaire qui va beaucoup nous aider pour ton onBoarding !" />
        <InscriptionBloomer
          me={dataMe}
          uploading={uploading}
          onSocietyDataValidated={upsertSocietyData}
          onBloomerDataValidated={upsertBloomerData}
        />
      </div>
    ) as React.ReactNode;
  }
  return (<FallbackError errorText="Ton invitation ne semble pas valide !" />) as React.ReactNode;
};

export default WithErrorBoundary(BloomerInvitation);
